import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  customerOrders, supplierOrders,
  type CustomerOrderListItem, type SupplierOrderListItem,
  type OrderKind,
} from '@/api/orders';
import { contractors as contractorsApi } from '@/api/catalogs';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { DOC_KIND_UA, type DocKind } from '@/api/goodsSales';
import DocListFilters, { ContractorFilter, DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

type Kind = 'customer' | 'supplier';
type Row = CustomerOrderListItem | SupplierOrderListItem;
type KindFilter = 'ALL' | OrderKind;

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OrdersPage({
  kind,
  forcedOrderKind,
  title: titleOverride,
  newButtonLabel,
}: {
  kind: Kind;
  /** ПРОДАЖ-розбивка Етап 1 (2026-06-03): якщо задано — UI без kind-фільтра, всі записи цього виду. */
  forcedOrderKind?: OrderKind;
  title?: string;
  newButtonLabel?: string;
}) {
  const orgNames = useOrgNames();
  const navigate = useNavigate();
  const isCustomer = kind === 'customer';
  // 2026-07-25: сторінка рахунків веде на СВІЙ маршрут /-invoices (а не
  // /-orders?kind=...) — інакше sidebar підсвічує «Замовлення» при
  // створенні/відкритті рахунку.
  const isInvoices = forcedOrderKind === 'PROFORMA_INVOICE';
  const baseRoute = isCustomer
    ? (isInvoices ? '/customer-invoices' : '/customer-orders')
    : (isInvoices ? '/supplier-invoices' : '/supplier-orders');
  const defaultTitle = isCustomer ? 'Замовлення покупців' : 'Замовлення постачальникам';
  const title = titleOverride ?? defaultTitle;

  const [kindFilter, setKindFilter] = useState<KindFilter>(
    forcedOrderKind ?? 'ALL',
  );
  // Стандартні фільтри списків (еталон /employees): орг + статус + дати.
  // Статус = закриття; default «Усі» (ч.792, рішення власника).
  const f = useDocListFilters();
  const closingFilter = f.status || 'ALL';
  // ч.792: фільтр за оплатою (Σ оплат з deal = замовлення).
  const [paidFilter, setPaidFilter] = useState<'' | 'paid' | 'unpaid' | 'partial'>('');
  // 2026-07-31 (рішення власника): пошуковий фільтр за контрагентом.
  const [contractorFilter, setContractorFilter] = useState('');
  // 0445: фільтр «Вид» за рядками (товари/послуги/обладнання/ОЗ) — лише покупці.
  const [docKind, setDocKind] = useState('');
  // 2026-08-09 (Норма #29): серверна пагінація + сортування (limit 200
  // ховав хвіст). Ключ datasource-deps мусить нести forcedOrderKind
  // (баг «залипання» списку рахунків — див. історію 2026-07-25).
  const { datasource, total } = useInfiniteGridDatasource<Row>(
    (limit, offset, sort) => {
      const params = {
        limit, offset,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        organization_id: f.orgId || undefined,
        contractor_id: contractorFilter || undefined,
        doc_kind: docKind || undefined,
        paid: paidFilter || undefined,
        date_from: f.dateFrom,
        date_to: f.dateTo,
        ...(kindFilter !== 'ALL' ? { order_kind: kindFilter } : {}),
        ...(closingFilter !== 'ALL'
          ? { closing_status: closingFilter as 'OPEN' | 'CLOSED' }
          : {}),
      };
      return isCustomer
        ? customerOrders.list(params)
        : supplierOrders.list(params);
    },
    [kind, forcedOrderKind ?? 'any', kindFilter, closingFilter, contractorFilter, docKind, paidFilter, ...f.queryKey],
    { attachmentsType: kind === 'customer' ? 'doc.customer_orders' : 'doc.supplier_orders' },
  );

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);

  const cols = useMemo<ColDef<Row>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: Row }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'contractor_id', headerName: isCustomer ? 'Покупець' : 'Постачальник',
      flex: 1, minWidth: 200, sortable: true,
      valueFormatter: (p) => contractorName.get(p.value) ?? '',
    },
    {
      field: 'delivery_date', headerName: 'Термін', width: 120, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'state', headerName: 'Стан', width: 130, sortable: false,
      cellRenderer: (p: { data?: Row }) => {
        const r = p.data;
        if (!r) return '';
        if (r.state === 'CANCELLED') {
          return <span style={{ color: '#dc2626' }}>скасований</span>;
        }
        if (r.closing_status === 'CLOSED') {
          const closedEarly = r.state !== 'COMPLETED';
          return (
            <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}
              title={(r.close_reason ? `Причина: ${r.close_reason}` : 'Закрито')
                + (closedEarly ? ' (закрито до повного виконання)' : '')}>
              закритий
            </span>
          );
        }
        if (r.state === 'COMPLETED') {
          return <span style={{ color: '#16a34a', fontWeight: 600 }}>виконаний</span>;
        }
        if (r.state === 'IN_PROGRESS') {
          return <span style={{ color: 'var(--badge-blue-fg)' }}>у роботі</span>;
        }
        return <span style={{ color: '#16a34a' }}>відкритий</span>;
      },
    },
    ...(isCustomer ? [{
      field: 'doc_kind', headerName: 'Вид', width: 110, sortable: false,
      valueFormatter: (p: { value?: string | null }) => p.value ? (DOC_KIND_UA[p.value as DocKind] ?? '') : '',
    } as ColDef<Row>] : []),
    {
      field: 'total_amount', headerName: 'Сума', width: 140, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'paid_amount', headerName: 'Оплата', width: 140, sortable: true,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: Row }) => {
        const r = p.data;
        if (!r) return '';
        const paid = Number(r.paid_amount ?? 0);
        const total = Number(r.total_amount);
        if (paid <= 0) return <span style={{ color: 'var(--muted)' }}>—</span>;
        const full = paid >= total && total > 0;
        return (
          <span style={{ color: full ? '#15803d' : '#b45309', fontWeight: 600 }}
            title={full ? 'Сплачено повністю' : `Сплачено ${fmt2(paid)} з ${fmt2(total)}`}>
            {fmt2(paid)}
          </span>
        );
      },
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 130, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [contractorName, isCustomer]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{title}<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate(
            `${baseRoute}/new${forcedOrderKind ? `?kind=${forcedOrderKind}` : ''}`,
          )}
        >
          + {newButtonLabel ?? 'Нове замовлення'}
        </button>
      </div>
      <DocListFiltersRow>
        {!forcedOrderKind && (
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
            data-testid="orders-kind-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі типи</option>
            <option value="ORDER">Замовлення</option>
            <option value="PROFORMA_INVOICE">Рахунки</option>
          </select>
        )}
        <DocListFilters f={f} compact statusOptions={[
          { value: 'ALL', label: 'Усі' },
          { value: 'OPEN', label: 'Відкриті' },
          { value: 'CLOSED', label: 'Закриті' },
        ]} />
        <select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value as '' | 'paid' | 'unpaid' | 'partial')}
          title="Оплата за документом" style={{ width: 130, flex: '0 0 auto' }}>
          <option value="">Оплата: усі</option>
          <option value="unpaid">Не оплачені</option>
          <option value="partial">Частково</option>
          <option value="paid">Оплачені</option>
        </select>
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter}
          label={isCustomer ? 'покупець' : 'постачальник'} width={210} />
        {isCustomer && (
          <select value={docKind} onChange={(e) => setDocKind(e.target.value)}
            title="Вид документа за рядками" style={{ width: 120, flex: '0 0 auto' }}>
            <option value="">Усі види</option>
            {(Object.keys(DOC_KIND_UA) as DocKind[]).map((k) => <option key={k} value={k}>{DOC_KIND_UA[k]}</option>)}
          </select>
        )}
      </DocListFiltersRow>
      <InfiniteDocGrid<Row>
        copyRow={(r) => navigate(`${baseRoute}/new?copy=${r.id}${forcedOrderKind ? `&kind=${forcedOrderKind}` : ''}`)}
        deleteRow={{ remove: (id) => (isCustomer ? customerOrders : supplierOrders).remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey={isCustomer ? 'customer-orders' : 'supplier-orders'}
        onRowDoubleClicked={(r) => navigate(`${baseRoute}/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
